import React, { useState, useEffect, useCallback } from "react";
import { COLORS } from "../../../../theme/colors";
import { MATERIALS } from "../../../../theme/materials";
import { agentApi, mcpApi, openapiApi } from "../../../../api/client";
import { ADMIN_ROLES } from "../../../../utils/helpers";
import TabBtn from "../../../../components/TabBtn";

// agent-experience-redesign-v1.md §6.3 — dual-mode bind modal for
// MCP + OpenAPI integrations (C3 + C4). One component handles both
// kinds via `kind` prop because the shape is identical:
//   tab (a) "Existing" — pick from tenant integrations + bind
//   tab (b) "Register new" — minimal form → create + auto-bind
//
// On success, calls onBound(integrationId) so the WiringTab can
// refetch and show the new binding.

const COPY = {
  mcp: {
    title: "Bind MCP server",
    listFn: () => mcpApi.listServers(),
    // mcpApi.listServers() returns { success, data: { servers: [...] } }
    // (mirrors AdminView.fetchMcpServers pattern). Defensive || [] keeps
    // the .filter() call safe even if the endpoint returns an empty body.
    listResultKey: (data) => data?.data?.servers || [],
    bindFn: (agentId, id) => agentApi.bindMcp(agentId, id),
    createFn: (payload) => mcpApi.registerServer(payload),
    createResultKey: (data) => data?.data?.id || data?.data?.server_id || data?.id,
    rowDisplay: (m) => ({
      id: m.id,
      title: m.name || m.server_id,
      sub: `${m.server_id} · ${m.connection_type}`,
      meta: m.description,
    }),
    fields: [
      { name: "server_id",       label: "Slug",            placeholder: "stripe-tools",        required: true,  hint: "Lowercase identifier; used in audit logs" },
      { name: "name",            label: "Name",            placeholder: "Stripe Tools",        required: true },
      { name: "description",     label: "Description",     placeholder: "What does this MCP do?", textarea: true },
      { name: "url",             label: "URL",             placeholder: "https://mcp.example.com", required: true },
      { name: "connection_type", label: "Connection type", placeholder: "stdio | http | sse",  required: true, defaultValue: "http" },
      { name: "auth_token",      label: "Auth token",      placeholder: "Bearer …",            secret: true },
    ],
  },
  openapi: {
    title: "Bind OpenAPI integration",
    listFn: () => openapiApi.list(),
    // openapiApi.list() returns { success, data: { integrations: [...] } }
    // (mirrors AdminView.fetchOpenapi pattern).
    listResultKey: (data) => data?.data?.integrations || data?.data || [],
    bindFn: (agentId, id) => agentApi.bindOpenapi(agentId, id),
    createFn: (payload) => openapiApi.create(payload),
    createResultKey: (data) => data?.data?.id || data?.id,
    rowDisplay: (o) => ({
      id: o.id,
      title: o.name,
      sub: `${o.slug} · ${o.auth_method}`,
      meta: o.base_url,
    }),
    fields: [
      { name: "name",          label: "Name",           placeholder: "Stripe API",                required: true },
      { name: "slug",          label: "Slug",           placeholder: "stripe-api",                required: true,  hint: "Lowercase identifier" },
      { name: "base_url",      label: "Base URL",       placeholder: "https://api.example.com",   required: true },
      { name: "spec_url",      label: "OpenAPI spec URL", placeholder: "https://api.example.com/openapi.yaml", required: true,
        hint: "Server fetches and previews the spec on save"
      },
      { name: "auth_method",   label: "Auth method",    placeholder: "bearer | api_key | basic | oauth2", defaultValue: "bearer" },
      { name: "credential_ref",label: "Credential field", placeholder: "stripe_api_key",
        hint: "Tenant settings field that stores the secret" },
    ],
  },
};

export default function BindIntegrationModal({ kind = "mcp", agentId, currentUser, onClose, onBound }) {
  const cfg = COPY[kind];
  // Backend gates POST /api/openapi-integrations on require_administrator
  // (router A1). When kind="openapi" and the user isn't admin/owner, hide
  // the "Register new" tab so they don't hit a silent 403. Binding to an
  // existing admin-created integration stays available.
  const canRegister = kind !== "openapi" || ADMIN_ROLES.includes(currentUser?.role);
  const [tab, setTab] = useState("existing");
  const [list, setList] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [boundIds, setBoundIds] = useState(new Set());
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  // Register-new form state
  const initialForm = {};
  for (const f of cfg.fields) initialForm[f.name] = f.defaultValue || "";
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  // Fetch the tenant list AND the agent's wiring (to skip already-bound).
  useEffect(() => {
    let cancelled = false;
    setLoadingList(true);
    Promise.all([
      cfg.listFn(),
      agentApi.getWiring(agentId).catch(() => null),
    ])
      .then(([listRes, wiring]) => {
        if (cancelled) return;
        const items = cfg.listResultKey(listRes) || [];
        setList(items);
        const wiringData = wiring?.data || {};
        const rows = kind === "mcp" ? (wiringData.mcp_servers || []) : (wiringData.openapi_integrations || []);
        const bound = new Set(rows.filter(r => r.bound).map(r => r.id));
        setBoundIds(bound);
      })
      .catch(e => { if (!cancelled) setError(e?.message || String(e)); })
      .finally(() => { if (!cancelled) setLoadingList(false); });
    return () => { cancelled = true; };
  }, [agentId, kind, cfg]);

  const bindExisting = useCallback(async (id) => {
    setBusyId(id);
    setError(null);
    try {
      await cfg.bindFn(agentId, id);
      onBound && onBound(id);
      onClose && onClose();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusyId(null);
    }
  }, [agentId, cfg, onBound, onClose]);

  const submitRegister = useCallback(async (e) => {
    e?.preventDefault?.();
    setError(null);
    // Required-field validation
    for (const f of cfg.fields) {
      if (f.required && !String(form[f.name] || "").trim()) {
        setError(`${f.label} is required`);
        return;
      }
    }
    setSubmitting(true);
    try {
      // Strip empty strings so optional fields don't override defaults.
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        if (v !== "" && v !== null && v !== undefined) payload[k] = v;
      }
      const created = await cfg.createFn(payload);
      const newId = cfg.createResultKey(created);
      if (!newId) throw new Error("Registration succeeded but no id returned");
      // Auto-bind the freshly registered integration to this agent.
      await cfg.bindFn(agentId, newId);
      onBound && onBound(newId);
      onClose && onClose();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setSubmitting(false);
    }
  }, [agentId, cfg, form, onBound, onClose]);

  const visibleList = list.filter(item => !boundIds.has(cfg.rowDisplay(item).id));

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        borderRadius: 12, width: "90%", maxWidth: 540, maxHeight: "85vh",
        overflow: "hidden", display: "flex", flexDirection: "column",
        boxShadow: MATERIALS?.spatialShadow ? MATERIALS.spatialShadow() : "0 16px 40px rgba(0,0,0,0.5)",
      }}>
        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{cfg.title}</div>
          <button type="button" onClick={onClose} style={{
            background: "transparent", border: "none", color: COLORS.textDim,
            cursor: "pointer", fontSize: 16,
          }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: `1px solid ${COLORS.border}`, background: COLORS.surface }}>
          <TabBtn active={tab === "existing"} onClick={() => setTab("existing")} label="Existing" />
          {canRegister && (
            <TabBtn active={tab === "create"} onClick={() => setTab("create")} label="Register new" />
          )}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
          {error && (
            <div style={{
              padding: "8px 12px", borderRadius: 6, marginBottom: 12,
              background: `${COLORS.pink || "#ef4444"}15`,
              border: `1px solid ${COLORS.pink || "#ef4444"}55`,
              color: COLORS.pink || "#ef4444", fontSize: 12,
            }}>{error}</div>
          )}

          {tab === "existing" && (
            <>
              {loadingList ? (
                <div style={{ color: COLORS.textDim, fontSize: 12 }}>Loading…</div>
              ) : visibleList.length === 0 ? (
                <div style={{ color: COLORS.textDim, fontSize: 12, lineHeight: 1.5 }}>
                  No unbound {kind === "mcp" ? "MCP servers" : "OpenAPI integrations"} in your tenant. Switch to <strong>Register new</strong> to add one.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {visibleList.map(item => {
                    const d = cfg.rowDisplay(item);
                    return (
                      <div key={d.id} style={{
                        padding: 12, borderRadius: 8,
                        border: `1px solid ${COLORS.border}`, background: COLORS.surface,
                        display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12,
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{d.title}</div>
                          {d.sub && <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2, fontFamily: "monospace" }}>{d.sub}</div>}
                          {d.meta && <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>{d.meta}</div>}
                        </div>
                        <button type="button"
                          disabled={busyId === d.id}
                          onClick={() => bindExisting(d.id)}
                          style={{
                            padding: "5px 14px", borderRadius: 6, border: "none",
                            background: COLORS.accent, color: "#fff",
                            fontSize: 11, fontWeight: 600, cursor: busyId === d.id ? "default" : "pointer",
                            opacity: busyId === d.id ? 0.6 : 1, whiteSpace: "nowrap",
                          }}>
                          {busyId === d.id ? "Binding…" : "Bind"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {tab === "create" && (
            <form onSubmit={submitRegister} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {cfg.fields.map(f => (
                <div key={f.name}>
                  <label style={{
                    display: "block", fontSize: 11, fontWeight: 600,
                    color: COLORS.textMuted, marginBottom: 4,
                    letterSpacing: "0.02em",
                  }}>
                    {f.label}{f.required && <span style={{ color: COLORS.pink || "#ef4444" }}> *</span>}
                  </label>
                  {f.textarea ? (
                    <textarea
                      value={form[f.name] || ""}
                      onChange={(e) => setForm(prev => ({ ...prev, [f.name]: e.target.value }))}
                      placeholder={f.placeholder}
                      rows={3}
                      style={fieldStyle}
                    />
                  ) : (
                    <input
                      type={f.secret ? "password" : "text"}
                      value={form[f.name] || ""}
                      onChange={(e) => setForm(prev => ({ ...prev, [f.name]: e.target.value }))}
                      placeholder={f.placeholder}
                      style={fieldStyle}
                    />
                  )}
                  {f.hint && (
                    <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 3 }}>{f.hint}</div>
                  )}
                </div>
              ))}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
                <button type="button" onClick={onClose} style={{
                  background: "transparent", border: `1px solid ${COLORS.border}`,
                  color: COLORS.textDim, padding: "6px 14px", borderRadius: 6,
                  fontSize: 12, cursor: "pointer",
                }}>Cancel</button>
                <button type="submit" disabled={submitting} style={{
                  background: COLORS.accent, color: "#fff", border: "none",
                  padding: "6px 14px", borderRadius: 6,
                  fontSize: 12, fontWeight: 600, cursor: submitting ? "default" : "pointer",
                  opacity: submitting ? 0.6 : 1,
                }}>
                  {submitting ? "Registering…" : "Register + Bind"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

const fieldStyle = {
  width: "100%", boxSizing: "border-box",
  padding: "8px 12px", fontSize: 12, color: COLORS.text,
  background: COLORS.surface,
  border: `1px solid ${COLORS.border}`, borderRadius: 6,
  outline: "none", fontFamily: "inherit",
};
